import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  effect,
  inject,
  input,
  OnChanges,
  OnInit,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';

import { FormArray } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { UploadedFileStatus } from '@vinnova/ui';
import { produce } from 'immer';
import {
  catchError,
  EMPTY,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  Subscription,
  take,
} from 'rxjs';
import { v7 as uuid } from 'uuid';
import { ReadonlyState } from '../../../core/models/form.model';
import { QuestionStructureFileUpload } from '../../../core/models/question/question-file-upload.model';
import { Question } from '../../../core/models/question/question.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../../core/repositories/renderer-forms.repository';
import {
  BaseFileService,
  FormUploadedFile,
} from '../../../core/services/base-file.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionNumberService } from '../../../core/services/question-number.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';
import { PreviousAnswersService } from '../../../core/services/previous-answers.service';

@UntilDestroy()
@Component({
  selector: 'lib-question-file-upload',
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class FileUploadComponent implements OnInit, OnChanges {
  repeaterGroupId = input<string>();
  allowedFileTypes = input<string>();
  labelPrefix = input<string>();
  valueFormControl = input<FormArray<FormUploadedFile>>();
  question = input<Question>();
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  fileUploaded: boolean;
  ReadonlyState = ReadonlyState;

  get title() {
    return this.question().title;
  }

  get description() {
    return this.question().structure.description;
  }

  get maxNumberOfFiles() {
    return this.question().validation.maxLength;
  }

  get required() {
    return this.question().validation.required;
  }

  get maxNumberOfPages() {
    return (this.question().structure as QuestionStructureFileUpload)
      .maxNumberOfPages;
  }

  get maxFileSize() {
    return +(this.question().structure as QuestionStructureFileUpload)
      .maxFileSize;
  }

  get isUpdatingAnswer() {
    return this.rendererFormRepository.getLoading();
  }

  fileList: FormUploadedFile[] = [];

  questionNumber$: Observable<unknown>;
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  isInvalid: WritableSignal<boolean> = signal(false);
  error: WritableSignal<boolean> = signal(false);
  math = Math;
  showChanges = input<boolean>();
  readonly previousAnswersService = inject(PreviousAnswersService);

  constructor(
    private formValidationService: FormValidationService,
    private fileService: BaseFileService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private questionNumberService: QuestionNumberService,
    private formsQuery: RendererFormsQuery,
    private rendererFormRepository: RendererFormsRepository,
  ) {
    effect(() => {
      this.fileList = this.valueFormControl().value ?? [];
    });
  }

  ngOnInit() {
    if (this.maxNumberOfPages) {
      this.valueFormControl().addValidators(
        ValidateFilePages(this.maxNumberOfPages),
      );
    }

    this.readOnly$ = this.formsQuery.selectReadOnly();
    this.activeLang$ = this.translocoService.langChanges$;
    this.formValidationService
      .questionInvalid(this.valueFormControl(), this.question().id)
      .pipe(untilDestroyed(this))
      .subscribe((val) => {
        if (this.fileUploaded || this.valueFormControl().errors?.filePages) {
          this.isInvalid.set(false);
        } else {
          this.isInvalid.set(val);
        }
      });

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.valueFormControl(),
      this.question(),
    );

    this.fileList = this.valueFormControl().value ?? [];
    this.cdr.markForCheck();

    this.questionNumber$ = this.questionNumberService.getQuestionNumberForLabel(
      this.question().id,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }

      // registers and listens on changes
      this.questionValueChangeService
        .valueChangeListener(
          this.valueFormControl(),
          {
            questionId: this.question().id,
            repeaterGroupId: this.repeaterGroupId(),
          },
          this.question(),
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(
          this.question(),
          this.repeaterGroupId(),
          this.valueFormControl(),
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.fileList = this.valueFormControl().value ?? [];
          this.cdr.markForCheck();
        });
    }
  }

  get uploadedFiles(): FormUploadedFile[] {
    return this.valueFormControl().value ?? [];
  }

  get isMaxNumberOfFilesReached(): boolean {
    return (
      this.maxNumberOfFiles &&
      this.maxNumberOfFiles <=
        this.fileList?.filter((f) =>
          [
            UploadedFileStatus.Uploaded,
            UploadedFileStatus.Loading,
            UploadedFileStatus.Success,
          ].includes(f.status),
        ).length
    );
  }

  get handlingstyp(): string {
    return ((this.question().structure as QuestionStructureFileUpload) ?? {})
      .handlingstyp;
  }

  get isUploadDisabled(): boolean {
    return this.isMaxNumberOfFilesReached;
  }

  public uploadFiles(files: File[]): void {
    if (this.isUploadDisabled) {
      return;
    }
    const numberOfFiles = this.uploadedFiles.length;
    if (
      this.maxNumberOfFiles &&
      numberOfFiles + files.length > this.maxNumberOfFiles
    ) {
      return;
    }

    for (const file of this.fileList) {
      if (
        !this.uploadedFiles.some((uf) => uf.uploadId === file.uploadId) &&
        file.status === UploadedFileStatus.Error
      ) {
        this.removeFileFromList(file.uploadId);
      }
    }

    // för att ladda flera filer
    // gör en array med observables och anropa apiet
    from(files)
      .pipe(
        mergeMap((f) => {
          const file = new File([f], this.convertExtensionToLowercase(f.name));
          const uploadId = this.addNewFileToList(file);
          this.rendererFormRepository.incrementUploadCount();
          if (
            !file.name.endsWith('.doc') &&
            !file.name.endsWith('.docx') &&
            !file.name.endsWith('.txt') &&
            !file.name.endsWith('.xls') &&
            !file.name.endsWith('.xlsx') &&
            !file.name.endsWith('.pdf') &&
            !file.name.endsWith('.rtf')
          ) {
            const index = this.getFileIndex(uploadId);
            const fileList = [...this.fileList];
            fileList[index].status = UploadedFileStatus.Error;
            fileList[index].validationMessageKey =
              'fileupload_validation_wrong_filetype';
            this.fileList = fileList;
            this.rendererFormRepository.decrementUploadCount();
            return EMPTY;
          }

          const megaBytes = file.size / 1024 / 1024;
          if (this.maxFileSize != undefined && megaBytes > this.maxFileSize) {
            const index = this.getFileIndex(uploadId);
            const fileList = [...this.fileList];
            fileList[index].status = UploadedFileStatus.Error;
            fileList[index].validationMessageKey =
              'fileupload_validation_max_size_exceeded';
            fileList[index].validationMessageParams = {
              size: this.maxFileSize,
            };
            this.fileList = fileList;
            this.rendererFormRepository.decrementUploadCount();
            return EMPTY;
          }

          const fileName =
            !this.maxNumberOfFiles || this.maxNumberOfFiles > 1
              ? `${this.title}_${this.getFileSuffix()}`
              : `${this.title}`;
          this.addFileNameToList(uploadId, fileName);

          return this.fileService.uploadFile(file, fileName).pipe(
            map((file) => {
              const status =
                file.pages > this.maxNumberOfPages && this.maxNumberOfPages > 0
                  ? UploadedFileStatus.ToManyPages
                  : UploadedFileStatus.Success;
              return {
                ...file,
                uploadId,
                handlingstyp: this.handlingstyp,
                validationMessageKey: 'fileupload_validation_maxpages',
                validationMessageParams: {
                  pages: file.pages,
                  maxpages: this.maxNumberOfPages,
                },
                status: status,
              };
            }),
            catchError(() => {
              const index = this.getFileIndex(uploadId);
              const fileList = [...this.fileList];
              fileList[index].status = UploadedFileStatus.Error;
              fileList[index].validationMessageKey =
                'fileupload_validation_error';
              this.fileList = fileList;
              return EMPTY;
            }),
            finalize(() => {
              // Decrement för varje fil som är klar (success eller error)
              this.rendererFormRepository.decrementUploadCount();
            }),
          );
        }),
        finalize(() => {
          // detta körs när alla filer har laddats upp
          this.fileList = this.fileList.map((f) =>
            f.status === UploadedFileStatus.Success
              ? { ...f, status: UploadedFileStatus.Uploaded }
              : f,
          );
          this.error.set(false);
          this.valueFormControl().markAsTouched();
          this.cdr.markForCheck();
        }),
        untilDestroyed(this),
      )
      .subscribe((result: FormUploadedFile) => {
        this.fileUploaded = true;
        const index = this.getFileIndex(result.uploadId);
        const fileList = [...this.fileList];
        fileList[index] = result;
        this.error.set(false);
        this.fileList = fileList;
        this.valueFormControl().markAsDirty();
        this.addToControl();
      });
  }

  public removeFile(file: FormUploadedFile): void {
    this.removeFileFromList(file.uploadId);
    if (this.question().validation.required && this.fileList.length === 0) {
      this.error.set(true);
    }
    this.fileService
      .deleteFile(file.pdfFileId)
      .pipe(
        take(1),
        finalize(() => {
          this.fileUploaded = false;
          this.valueFormControl().markAsTouched();
          this.cdr.markForCheck();
        }),
      )
      .subscribe();
  }

  public downloadFile(file: FormUploadedFile): void {
    this.fileService
      .downloadFile(file.pdfFileId, file.pdfFileName)
      .pipe(
        take(1),
        finalize(() => {
          this.valueFormControl().markAsTouched();
        }),
      )
      .subscribe();
  }

  public fileWindow(isOpen) {
    this.error.set(false);
    if (
      !isOpen &&
      !this.fileList.some((f) => f.status === UploadedFileStatus.Loading)
    ) {
      this.valueFormControl().markAsTouched();
      this.valueFormControl().markAsDirty();
      if (this.question().validation.required && this.fileList.length === 0) {
        this.error.set(true);
      }
    }
    this.cdr.detectChanges();
  }

  private removeFileFromList(uploadId: string) {
    this.fileList = this.fileList.filter((f) => f.uploadId !== uploadId);
    this.valueFormControl().markAsDirty();
    this.valueFormControl().markAsTouched();
    this.addToControl();
  }

  private getFileIndex(uploadId: string): number {
    return this.fileList.findIndex((file) => file.uploadId === uploadId);
  }

  private addNewFileToList(file: File): string {
    const newFile = {
      originalFileName: file.name,
      uploadId: uuid(),
      status: UploadedFileStatus.Loading,
    } as FormUploadedFile;
    const fileList = [...this.fileList, newFile];

    this.fileList = fileList;
    return newFile.uploadId;
  }

  private addFileNameToList(uploadId: string, fileName: string) {
    const index = this.getFileIndex(uploadId);
    const fileList = [...this.fileList];
    fileList[index].pdfFileName = fileName;
    this.fileList = fileList;
  }

  private getFileSuffix() {
    const fileList = [...this.fileList];
    const sorted = fileList
      .filter((f) => f.pdfFileName?.includes('_'))
      .map((f) => {
        let fileName = f.pdfFileName;
        if (fileName.includes('.')) {
          fileName = fileName.split('.')[0];
        }
        const split = fileName.split('_');
        const suffixString = split.at(-1);
        return isNaN(suffixString as unknown as number)
          ? undefined
          : +suffixString;
      })
      .sort((a, b) => (a > b ? -1 : 1));
    return sorted.length === 0 ? '1' : (sorted[0] + 1).toString();
  }

  private addToControl() {
    const filtered = produce(this.fileList, (draft) => {
      return draft.filter(
        (f) =>
          f.status === UploadedFileStatus.Success ||
          f.status === UploadedFileStatus.Uploaded ||
          f.status === UploadedFileStatus.ToManyPages,
      );
    });
    this.valueFormControl().patchValue(filtered);
  }

  private convertExtensionToLowercase(filename: string) {
    const extensionIndex = filename.lastIndexOf('.');

    if (extensionIndex === -1) {
      return filename;
    }

    const name = filename.slice(0, extensionIndex);
    const extension = filename.slice(extensionIndex + 1);

    return name + '.' + extension.toLowerCase();
  }
}

export function ValidateFilePages(maxNumberOfPages: number) {
  return (formArray: FormArray<FormUploadedFile>) => {
    if (formArray.value) {
      let valid = true;
      for (const file of formArray.value) {
        if (file.pages > maxNumberOfPages) {
          valid = false;
          break;
        }
      }
      return valid ? undefined : { filePages: true };
    }
  };
}
